import React, { useState } from 'react';
import { MarAsset } from 'types/rest/marFilesTypes';
import Row from 'react-bootstrap/Row';
import { Col, Form } from 'react-bootstrap';
import styles from './MarFileExpandedDetail.module.css';
import i18n from 'i18n';
import Button from 'react-bootstrap/Button';
import ServicePicker from 'components/ui/servicePicker/ServicePicker';
import TagSelector from 'components/selectors/TagSelector';
import _ from 'lodash';
import { isDirty } from 'utils/assetUtils';
import MarFileAssetView from 'views/AssetCatalog/MarFiles/MarsTable/MarFileAssetView/MarFileAssetView';
import { Service, Tag } from 'types/rest/assetCatalogTypes';
import { dateAndAuthorFormat } from 'utils/dateUtil';

interface LocalProps {
  marFile: MarAsset;
  updateMarFile: any;
  isReadOnly?: boolean;
}

type MarFileField = 'serviceId' | 'serviceName' | 'description' | 'tags';

const MarFileExpandedDetailItem = (props: LocalProps) => {
  const [marFileState, setMarFileState] = useState(props.marFile);
  const [isMarFileDirty, setMarFileDirty] = useState(!props.marFile); // indicates whether a change was made in the editor to enable Save button

  const handleMarFileChange = (e: any, field: MarFileField) => {
    const target = e.target as HTMLInputElement;
    updateMarFileState(field, target.value);
  };

  const updateMarFileState = (field: MarFileField, value: any) => {
    const marFile = _.cloneDeep(marFileState);
    _.set(marFile, field, value);
    setMarFileDirty(isDirty(marFile));
    setMarFileState(marFile);
  };

  const updateMarFileStateTwoFields = (field1: MarFileField, value1: any, field2: MarFileField, value2: any) => {
    const marFile = _.cloneDeep(marFileState);
    _.set(marFile, field1, value1);
    _.set(marFile, field2, value2);
    setMarFileDirty(isDirty(marFile));
    setMarFileState(marFile);
  };

  const handleServiceChange = (vs: Service | null) => {
    updateMarFileStateTwoFields(
      'serviceId',
      vs && vs.id != null ? vs.id : 0,
      'serviceName',
      vs && vs.name != null ? vs.name : '',
    );
  };

  const tagSelectHandler = (tags: Tag[]) => {
    updateMarFileState(
      'tags',
      tags.map(t => t.name),
    );
  };

  const saveMarFile = () => {
    props.updateMarFile(marFileState);
  };
  const createdMessage = dateAndAuthorFormat(props.marFile.created, props.marFile.createdBy);
  const updatedMessage = dateAndAuthorFormat(props.marFile.updated, props.marFile.updatedBy);

  return (
    <div className={styles.metadataRow}>
      <Row>
        <Col xs={2}>
          <div className="form-group-heading justify-content-start">
            <Form.Label>{i18n.t('marFiles:detail.createdTitle')}</Form.Label>
          </div>
          <div className="mt-1 text-truncate">
            <span title={createdMessage}> {createdMessage}</span>
          </div>
        </Col>

        <Col xs={2}>
          <div className="form-group-heading">
            <Form.Label>{i18n.t('marFiles:detail.updatedTitle')}</Form.Label>
          </div>
          <div className="mt-1 text-truncate">
            <span title={updatedMessage}> {updatedMessage}</span>
          </div>
        </Col>

        <Col xs={2} className="qa-think-time-percent">
          <div className="form-group-heading">
            <Form.Label>{i18n.t('marFiles:detail.thinkTimePercent')}</Form.Label>
          </div>
          <div>
            {_.get(props, 'marFile.primaryMetadata.thinkTimePercent')
              ? props.marFile.primaryMetadata!.thinkTimePercent + '%'
              : '-'}
          </div>
        </Col>

        <Col xs={2} className="qa-concurrent-capacity">
          <div className="form-group-heading">
            <Form.Label>{i18n.t('marFiles:detail.concurrentCapacity')}</Form.Label>
          </div>
          <div>
            {_.get(props, 'marFile.primaryMetadata.concurrentCapacity')
              ? props.marFile.primaryMetadata!.concurrentCapacity
              : '-'}
          </div>
        </Col>

        <Col xs={2} className="qa-group-tag">
          <div className="form-group-heading">
            <Form.Label>{i18n.t('marFiles:detail.groupTag')}</Form.Label>
          </div>
          <div>{_.get(props, 'marFile.primaryMetadata.groupTag') ? props.marFile.primaryMetadata!.groupTag : '-'}</div>
        </Col>

        <Col xs={2} className="text-md-right">
          <Button
            disabled={!isMarFileDirty || props.isReadOnly}
            onClick={() => saveMarFile()}
            id="qa-btn-save-marfile"
            title={i18n.t('marFiles:detail.saveButtonTitle')}
          >
            {i18n.t('common:actions.save')}
          </Button>
        </Col>
      </Row>

      <Row className="mt-5">
        <Col md={4}>
          <Form.Group>
            <div className="form-group-heading justify-content-start">
              <Form.Label htmlFor={'qa-marfile-service-picker-' + props.marFile.id}>
                {i18n.t('marFiles:detail.serviceTitle')}
              </Form.Label>
            </div>
            <ServicePicker
              isCreateEnabled={true}
              id={'qa-marfile-service-picker-' + props.marFile.id}
              manualPreLoad={true}
              selectHandler={handleServiceChange}
              selectionDisabled={props.isReadOnly}
              isSelectAll={false}
              preSelected={marFileState.serviceId}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label htmlFor="react-select-multiple">{i18n.t('marFiles:detail.tagsTitle')}</Form.Label>
            <TagSelector
              isReadOnly={props.isReadOnly}
              preSelected={marFileState.tags}
              selectHandler={tagSelectHandler}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label htmlFor={'qa-marfile-description-field-' + props.marFile.id}>
              {i18n.t('marFiles:detail.descriptionTitle')}
            </Form.Label>
            <Form.Control
              as="input"
              disabled={props.isReadOnly}
              id={'qa-marfile-description-field-' + props.marFile.id}
              onChange={event => handleMarFileChange(event, 'description')}
              placeholder={i18n.t('marFiles:detail.enterDescription')}
              value={marFileState.description ? marFileState.description : ''}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* SecondaryMetadata */}
      <MarFileAssetView marAssetId={props.marFile.id} />
    </div>
  );
};

export default MarFileExpandedDetailItem;
